import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '@/hooks';
import { setSecretSortBy } from '@/modules/secrets/services/actions';
import type { SortBy } from '@/modules/secrets/services/reducers';
import { selectSecrets } from '@/modules/secrets/services/selectors';

import { SelectFilter } from '../SelectFilter';

type SecretSortByValue = 'nameDesc' | 'nameAsc' | 'updatedAtDesc' | 'updatedAtAsc' | 'createdAtDesc' | 'createdAtAsc';

export const SortByFilter = () => {
    const { options, optionsMap } = useMemo(() => {
        const optionsMap = new Map<SecretSortByValue, SortBy>([
            ['nameAsc', { order: 'asc', field: 'name', comparator: 'string' }],
            ['nameDesc', { order: 'desc', field: 'name', comparator: 'string' }],
            ['updatedAtDesc', { order: 'desc', field: 'updatedAt', comparator: 'date' }],
            ['updatedAtAsc', { order: 'asc', field: 'updatedAt', comparator: 'date' }],
            ['createdAtDesc', { order: 'desc', field: 'createdAt', comparator: 'date' }],
            ['createdAtAsc', { order: 'asc', field: 'createdAt', comparator: 'date' }],
        ]);

        const options = Array.from(optionsMap.entries()).map(([id]) => ({
            label: <FormattedMessage id={`secret.sortBy.${id}`} />,
            value: id,
        }));

        return { options, optionsMap };
    }, []);
    type Option = (typeof options)[number];
    const dispatch = useDispatch();
    const { sortBy } = useAppSelector(selectSecrets);
    const [value, setValue] = useState<Option['value']>(() => {
        for (const [id, option] of optionsMap.entries()) {
            if (option.field === sortBy.field && option.order === sortBy.order) {
                return id;
            }
        }

        return options[0].value;
    });

    return (
        <SelectFilter
            label={{ id: 'secret.filter.sortBy' }}
            value={value}
            onChange={(value: Option['value']) => {
                const option = optionsMap.get(value as SecretSortByValue)!;
                dispatch(setSecretSortBy(option));
                setValue(value);
            }}
            options={options}
        />
    );
};
